import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

/**
 * FailoverPolicyDialog — politique de fraîcheur CONFIGURABLE des tests de bascule.
 * Si désactivée : aucune pénalité automatique n'est appliquée à la confiance.
 */
export default function FailoverPolicyDialog({ open, onOpenChange }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const writable = can("dependency:write");
  const [form, setForm] = useState({ enabled: false, frequency_days: "", warning_days: "", expiry_days: "" });

  const { data } = useQuery({
    queryKey: ["failover-policy"],
    queryFn: async () => (await api.get("/failover/policy")).data,
    enabled: open,
  });

  useEffect(() => {
    if (data) setForm({
      enabled: !!data.enabled,
      frequency_days: data.frequency_days ?? "",
      warning_days: data.warning_days ?? "",
      expiry_days: data.expiry_days ?? "",
    });
  }, [data]);

  const save = useMutation({
    mutationFn: async () => (await api.put("/failover/policy", {
      enabled: form.enabled,
      frequency_days: form.frequency_days ? Number(form.frequency_days) : null,
      warning_days: form.warning_days ? Number(form.warning_days) : null,
      expiry_days: form.expiry_days ? Number(form.expiry_days) : null,
    })).data,
    onSuccess: () => {
      toast.success("Politique enregistrée");
      qc.invalidateQueries({ queryKey: ["failover-policy"] });
      qc.invalidateQueries({ queryKey: ["alternatives"] });
      qc.invalidateQueries({ queryKey: ["service-impact"] });
      onOpenChange(false);
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border rounded-sm" data-testid="failover-policy-dialog">
        <DialogHeader>
          <DialogTitle className="font-head">Politique de test de bascule</DialogTitle>
          <DialogDescription>
            Politique de fraîcheur configurable. CROS n'impose aucun seuil universel : si elle est désactivée,
            la date du dernier test est affichée sans réduire automatiquement la confiance.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="flex items-center justify-between border border-border rounded-sm p-3">
            <div>
              <Label className="text-sm">Activer la politique</Label>
              <p className="text-[11px] text-muted-foreground">Désactivée = aucune pénalité automatique.</p>
            </div>
            <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} disabled={!writable} data-testid="policy-enabled-switch" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-xs">Fréquence attendue (j)</Label><Input type="number" min="1" value={form.frequency_days} onChange={(e) => setForm({ ...form, frequency_days: e.target.value })} className="mt-1 rounded-sm" data-testid="policy-frequency" disabled={!form.enabled || !writable} /></div>
            <div><Label className="text-xs">Seuil d'avertissement (j)</Label><Input type="number" min="1" value={form.warning_days} onChange={(e) => setForm({ ...form, warning_days: e.target.value })} className="mt-1 rounded-sm" data-testid="policy-warning" disabled={!form.enabled || !writable} /></div>
            <div><Label className="text-xs">Seuil d'expiration (j)</Label><Input type="number" min="1" value={form.expiry_days} onChange={(e) => setForm({ ...form, expiry_days: e.target.value })} className="mt-1 rounded-sm" data-testid="policy-expiry" disabled={!form.enabled || !writable} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Fermer</Button>
          {writable && <Button className="rounded-sm" onClick={() => save.mutate()} disabled={save.isPending} data-testid="save-policy-button">Enregistrer</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
